"use client";

import { useCallback, useEffect, useRef } from "react";

/** Speculative work owns its cancellation and never participates in message sending. */
export function usePlannerPrewarm(
  request: ((signal: AbortSignal) => Promise<unknown>) | null,
  enabled: boolean
) {
  const requestRef = useRef(request);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);
  const cleanupRef = useRef<(() => void) | null>(null);
  const available = request !== null;

  const prewarm = useCallback(() => {
    cleanupRef.current?.();
    const send = requestRef.current;
    if (!send) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    const cleanup = () => {
      clearTimeout(timeout);
      controller.abort();
    };
    cleanupRef.current = cleanup;
    void Promise.resolve()
      .then(() => {
        return controller.signal.aborted ? undefined : send(controller.signal);
      })
      .catch(() => {
        // Warmup is optional, including when an older backend has no endpoint yet.
      })
      .finally(() => {
        clearTimeout(timeout);
        if (cleanupRef.current === cleanup) cleanupRef.current = null;
      });
  }, []);

  useEffect(() => {
    if (enabled && available) prewarm();
    return () => cleanupRef.current?.();
  }, [enabled, available, prewarm]);

  return prewarm;
}
